import {
  FirstDayOfWeekEnum,
  type Task,
  formatDayNumber,
  isSameDate,
  parseDate,
  toDateString,
} from "@taskfortress/core"
import React, { useCallback, useMemo, useState } from "react"
import { cn } from "../../styles/utils"
import type { PropsWithClassname } from "../../types"
import { Box } from "../Box"
import { Button } from "../Button"
import { Text } from "../Text"
import { Pressable } from "../gluestack/pressable"

interface DatePickerProps extends PropsWithClassname {
  options: string[] | number[]
  onSelect: (value: any) => void
  currentValue: string | number
  formatOption?: (option: string | number) => string
}

const BaseDatePicker: React.FC<DatePickerProps> = ({
  options,
  onSelect,
  currentValue,
  formatOption,
  className,
}) => {
  const handleOptionSelect = useCallback(
    (option: string | number) => {
      onSelect(option)
    },
    [onSelect],
  )

  return (
    <Box
      className={cn(
        "absolute left-1/2 transform -translate-x-1/2 mt-1 bg-background-0 shadow-lg rounded-lg p-2 z-50 w-48 max-h-48 overflow-y-auto",
        className,
      )}
    >
      <Box className="grid grid-cols-3 gap-1">
        {options.map((option) => (
          <Button
            key={option}
            onPress={() => handleOptionSelect(option)}
            variant={option === currentValue ? "primary" : "ghost"}
            className="text-xs p-1 rounded hover:bg-accent"
          >
            <Text className="text-xs">
              {formatOption ? formatOption(option) : option}
            </Text>
          </Button>
        ))}
      </Box>
    </Box>
  )
}

const MonthPicker: React.FC<{
  selectedMonth: number
  onMonthSelect: (month: number) => void
  months: string[]
}> = ({ selectedMonth, onMonthSelect, months }) => {
  return (
    <BaseDatePicker
      options={months.map((_, index) => index)}
      onSelect={onMonthSelect}
      currentValue={selectedMonth}
      formatOption={(monthIndex) =>
        months[monthIndex as number].substring(0, 3)
      }
    />
  )
}

const YearPicker: React.FC<{
  selectedYear: number
  onYearSelect: (year: number) => void
  years: number[]
}> = ({ selectedYear, onYearSelect, years }) => {
  return (
    <BaseDatePicker
      options={years}
      onSelect={onYearSelect}
      currentValue={selectedYear}
    />
  )
}

interface DatePickerCoreProps extends PropsWithClassname {
  selectedDate: string
  onDateSelect: (date: string) => void
  minDate?: string
  maxDate?: string
  disabledDates?: string[]
  showTodayButton?: boolean
  showTaskIndicators?: boolean
  tasks?: Task[]
  firstDayOfWeek: FirstDayOfWeekEnum
  headerContainerClassName?: string
  todayButtonContainerClassName?: string
  todayButtonClassName?: string
  todayButtonTextClassName?: string
  daysOfWeekContainerClassName?: string
  cellContainerClassName?: string
}

export const DatePickerCore: React.FC<DatePickerCoreProps> = ({
  selectedDate,
  onDateSelect,
  minDate,
  maxDate,
  disabledDates = [],
  showTodayButton = false,
  showTaskIndicators = false,
  tasks = [],
  firstDayOfWeek,
  className,
  headerContainerClassName,
  todayButtonContainerClassName,
  todayButtonClassName,
  todayButtonTextClassName,
  daysOfWeekContainerClassName,
  cellContainerClassName,
}) => {
  const [displayedDate, setDisplayedDate] = useState(selectedDate)
  const [showYearPicker, setShowYearPicker] = useState(false)
  const [showMonthPicker, setShowMonthPicker] = useState(false)

  // Update displayed date when selectedDate changes
  React.useEffect(() => {
    setDisplayedDate(selectedDate)
  }, [selectedDate])

  const parsedDate = useMemo(() => parseDate(displayedDate), [displayedDate])

  // Dynamic days of week based on firstDayOfWeek setting
  const daysOfWeek = useMemo(() => {
    if (firstDayOfWeek === FirstDayOfWeekEnum.MONDAY) {
      return ["M", "T", "W", "T", "F", "S", "S"]
    } else {
      return ["S", "M", "T", "W", "T", "F", "S"]
    }
  }, [firstDayOfWeek])

  const calendarCells = useMemo(() => {
    const { year, month } = parsedDate

    const firstDay = new Date(year, month, 1)
    let firstDayOfWeekNum = firstDay.getDay()

    // Adjust for Monday first week
    if (firstDayOfWeek === FirstDayOfWeekEnum.MONDAY) {
      // Convert Sunday (0) to 6, and shift others down by 1
      firstDayOfWeekNum = firstDayOfWeekNum === 0 ? 6 : firstDayOfWeekNum - 1
    }

    const lastDay = new Date(year, month + 1, 0)
    const lastDate = lastDay.getDate()

    const prevMonthDays = []
    const prevMonth = new Date(year, month, 0)
    const prevMonthLastDate = prevMonth.getDate()

    for (let i = 0; i < firstDayOfWeekNum; i++) {
      const day = prevMonthLastDate - firstDayOfWeekNum + i + 1
      prevMonthDays.push(toDateString(new Date(year, month - 1, day)))
    }

    const currentMonthDays = []
    for (let i = 1; i <= lastDate; i++) {
      currentMonthDays.push(toDateString(new Date(year, month, i)))
    }

    const nextMonthDays = []
    const remainingCells = 42 - (prevMonthDays.length + currentMonthDays.length)

    for (let i = 1; i <= remainingCells; i++) {
      nextMonthDays.push(toDateString(new Date(year, month + 1, i)))
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays]
  }, [parsedDate, firstDayOfWeek])

  const { monthFormatted, yearFormatted } = useMemo(
    () => ({
      monthFormatted: parsedDate.date.toLocaleDateString("en-US", {
        month: "long",
      }),
      yearFormatted: parsedDate.date.toLocaleDateString("en-US", {
        year: "numeric",
      }),
    }),
    [parsedDate.date],
  )

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleDateString("en-US", { month: "long" }),
    )
  }, [])

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 21 }, (_, i) => currentYear - 10 + i)
  }, [])

  const isCurrentMonth = useCallback(
    (date: string): boolean => {
      const { year, month } = parseDate(date)
      return year === parsedDate.year && month === parsedDate.month
    },
    [parsedDate],
  )

  const isToday = useCallback((date: string): boolean => {
    const today = new Date()
    return isSameDate(date, today)
  }, [])

  const isDateSelected = useCallback(
    (date: string): boolean => {
      return isSameDate(selectedDate, date)
    },
    [selectedDate],
  )

  const isDateDisabled = useCallback(
    (date: string): boolean => {
      if (disabledDates.includes(date)) return true
      if (minDate && date < minDate) return true
      if (maxDate && date > maxDate) return true
      return false
    },
    [disabledDates, minDate, maxDate],
  )

  const handlePrevMonth = useCallback(() => {
    const newDate = new Date(parsedDate.year, parsedDate.month - 1, 1)
    const newDateString = toDateString(newDate)
    setDisplayedDate(newDateString)
  }, [parsedDate])

  const handleNextMonth = useCallback(() => {
    const newDate = new Date(parsedDate.year, parsedDate.month + 1, 1)
    const newDateString = toDateString(newDate)
    setDisplayedDate(newDateString)
  }, [parsedDate])

  const handleDayClick = useCallback(
    (date: string) => {
      if (isDateDisabled(date)) return
      onDateSelect(date)
    },
    [onDateSelect, isDateDisabled],
  )

  const handleYearSelect = useCallback(
    (year: number) => {
      const newDate = new Date(year, parsedDate.month, 1)
      const newDateString = toDateString(newDate)
      setDisplayedDate(newDateString)
      setShowYearPicker(false)
    },
    [parsedDate],
  )

  const handleMonthSelect = useCallback(
    (month: number) => {
      const newDate = new Date(parsedDate.year, month, 1)
      const newDateString = toDateString(newDate)
      setDisplayedDate(newDateString)
      setShowMonthPicker(false)
    },
    [parsedDate],
  )

  const handleMonthPickerToggle = useCallback(() => {
    setShowMonthPicker((prev) => !prev)
    setShowYearPicker(false)
  }, [])

  const handleYearPickerToggle = useCallback(() => {
    setShowYearPicker((prev) => !prev)
    setShowMonthPicker(false)
  }, [])

  const handleCellPress = useCallback(
    (date: string) => {
      return () => handleDayClick(date)
    },
    [handleDayClick],
  )

  const handleTodayClick = useCallback(() => {
    const today = new Date()
    const todayString = toDateString(today)
    onDateSelect(todayString)
    setDisplayedDate(todayString)
  }, [onDateSelect])

  const hasTasksOnDate = useCallback(
    (date: string): boolean => {
      if (!showTaskIndicators || !tasks) return false
      return tasks.some((task: Task) => task.dueDate === date)
    },
    [showTaskIndicators, tasks],
  )

  return (
    <Box className={cn("bg-background-0 rounded-lg shadow-sm", className)}>
      <Box
        className={cn(
          "text-center py-1 px-1 border-b border-outline-200",
          headerContainerClassName,
        )}
      >
        <Box className="flex-row items-center justify-between">
          <Button
            onPress={handlePrevMonth}
            variant="ghost"
            aria-label="Previous month"
          >
            <Text>←</Text>
          </Button>
          <Box className="flex-row items-center">
            <Button
              onPress={handleMonthPickerToggle}
              variant="ghost"
              className="px-1"
            >
              <Text className="text-xs">{monthFormatted}</Text>
            </Button>
            <Button
              onPress={handleYearPickerToggle}
              variant="ghost"
              className="px-1"
            >
              <Text className="text-xs">{yearFormatted}</Text>
            </Button>
          </Box>
          <Button
            onPress={handleNextMonth}
            variant="ghost"
            aria-label="Next month"
          >
            <Text>→</Text>
          </Button>
        </Box>
      </Box>

      {showTodayButton && (
        <Box
          className={cn(
            "px-3 py-2 border-b border-outline-200",
            todayButtonContainerClassName,
          )}
        >
          <Button
            onPress={handleTodayClick}
            variant="ghost"
            className={cn("w-full", todayButtonClassName)}
          >
            <Text
              className={cn(
                "text-xs text-primary-600",
                todayButtonTextClassName,
              )}
            >
              Today
            </Text>
          </Button>
        </Box>
      )}

      <Box className="relative">
        <Box
          className={cn(
            "flex-row mb-4 px-3 pt-3",
            daysOfWeekContainerClassName,
          )}
        >
          {daysOfWeek.map((day, i) => (
            <Box key={i} className="flex-1 items-center">
              <Text className="text-xs font-bold text-typography-700">
                {day}
              </Text>
            </Box>
          ))}
        </Box>

        <Box
          className={cn("flex-row flex-wrap px-3 pb-3", cellContainerClassName)}
        >
          {calendarCells.map((date, index) => {
            const isSelected = isDateSelected(date)
            const isTodayDate = isToday(date)
            const inCurrentMonth = isCurrentMonth(date)
            const isDisabled = isDateDisabled(date)
            const hasTasks = hasTasksOnDate(date)

            return (
              <Pressable
                key={index}
                onPress={handleCellPress(date)}
                style={{ width: "14.28%" }}
                className="h-8 items-center justify-center"
                disabled={isDisabled}
              >
                <Box
                  className={cn(
                    "w-7 h-7 rounded-full items-center justify-center border border-transparent",
                    !inCurrentMonth && "opacity-40",
                    isTodayDate && "bg-outline-600",
                    isSelected &&
                      "border-primary-400 bg-primary-200 opacity-80",
                    isDisabled && "opacity-30",
                    !isDisabled && "hover:border-primary-800",
                  )}
                >
                  <Text
                    className={cn(
                      "text-xs",
                      isSelected
                        ? "text-primary-500 font-medium"
                        : "text-typography-800",
                      isDisabled && "text-typography-400",
                    )}
                  >
                    {formatDayNumber(date)}
                  </Text>
                  {hasTasks && (
                    <Box className="absolute -bottom-0.5 w-1 h-0.5 bg-typography-800 rounded-full border border-dashed border-typography-800" />
                  )}
                </Box>
              </Pressable>
            )
          })}
        </Box>
        {showMonthPicker && (
          <MonthPicker
            selectedMonth={parsedDate.month}
            onMonthSelect={handleMonthSelect}
            months={months}
          />
        )}

        {showYearPicker && (
          <YearPicker
            selectedYear={parsedDate.year}
            onYearSelect={handleYearSelect}
            years={years}
          />
        )}
      </Box>
    </Box>
  )
}
